import { defineStore } from 'pinia';
import NotificationService from 'src/services/notification-service';
import { NotificationReceived } from 'src/types/notification/notification';
import { useWebSocketStore } from './websocket-store';
import { useProposalStore } from './proposal-store';
import { ApplicationStatusType_Enum, LoanStatusType_Enum } from 'src/types/enums';
import { AppStateChangedContent } from 'src/types/notification/appStateChangedContent';
import { AppReceivedContent } from 'src/types/notification/appReceivedContent';

const notificationSound = new Audio(require('assets/sounds/notification.mp3'));

export const useNotificationStore = defineStore('notification', {
	state: () => ({
		notifications: [] as NotificationReceived[],
		token: null as string | null,
		isLoggedIn: false as boolean,
		enableSound: true as boolean,
	}),
	getters:{
		count: (state) => state.notifications.length
	},
	actions:{
		async getToken(aid: string) {
			return new Promise<void>(async (resolve, reject) => {
				const response = await NotificationService.getAuthenticationToken(aid);
				this.token = response;
				resolve();
			})
		},
		addNotification(notification: NotificationReceived, aid?: string) {
			this.notifications.unshift(notification);
			if(this.enableSound){
				notificationSound.play().catch((error) => {
					console.error('Error playing notification sound:', error);
				})
			}
			switch (notification.scenario) {
				case "ApplicationStateChanged":
				case "NotProceeding":
					const c = JSON.parse(notification.content) as AppStateChangedContent;
					useProposalStore().updateState(c.applicationId, LoanStatusType_Enum[c.applicationState as keyof typeof LoanStatusType_Enum]);
					break;
				case "Proceeding":
					const p = JSON.parse(notification.content);
					useProposalStore().updateState(p.applicationId, LoanStatusType_Enum['Ready To Deal']);
					break;
				case "UnableToContact":
					const u = JSON.parse(notification.content);
					useProposalStore().updateState(u.applicationId, LoanStatusType_Enum['Unable To Contact']);
					break;
				case "ApplicationReceived":
					const a = JSON.parse(notification.content) as AppReceivedContent;
					useProposalStore().getApplication(a.applicationId);
					break;
				case "DealerDocsOut":
					const d = JSON.parse(notification.content);
					useProposalStore().updateState(d.applicationId, LoanStatusType_Enum['Awaiting Payout']);
					break;
				case "Payout":
					const payout = JSON.parse(notification.content);
					useProposalStore().updateState(payout.applicationId, LoanStatusType_Enum['Awaiting Payout']);
					break;
				case "Dealt":
					const dealt = JSON.parse(notification.content);
					useProposalStore().updateState(dealt.applicationId, LoanStatusType_Enum.Completed);
					break;
				default:
					break;
			}
		},
		setLoggedIn(value: boolean) {
			this.isLoggedIn = value;
		},
		invalidateToken() {
			this.token = null;
		}
	},
	persist: true
});
